// Pure game data — recipes, village requests, the codex, chapters and prayers.
// No engine imports: `verify.ts` loads this headlessly. All numbers referenced
// live here or in config.ts.

export type MaterialKey = 'wood' | 'timber' | 'stone' | 'rope' | 'cloth' | 'peg';

export const MATERIALS: Record<MaterialKey, { name: string; short: string; color: string }> = {
  wood:   { name: 'Wood',        short: 'Wood',   color: '#c68a4e' },
  timber: { name: 'Good Timber', short: 'Timber', color: '#a9743f' },
  stone:  { name: 'Stone',       short: 'Stone',  color: '#9b9384' },
  rope:   { name: 'Rope',        short: 'Rope',   color: '#c9b06a' },
  cloth:  { name: 'Cloth',       short: 'Cloth',  color: '#c9553f' },
  peg:    { name: 'Pegs',        short: 'Pegs',   color: '#8a5a34' },
};

export interface Recipe {
  id: string;
  name: string;
  cost: Partial<Record<MaterialKey, number>>;
  major: boolean;            // major pieces run the 3-step quality mini-game
  craftsmanship: number;     // base craftsmanship award (before quality bonus)
  minLevel: number;          // workshop level required (1 = basic bench)
  desc: string;
  /** Materials this recipe PRODUCES, rather than a deliverable product. An
   *  intermediate good — pegs, and anything like them later. */
  yields?: Partial<Record<MaterialKey, number>>;
}

// Kept intentionally small and meaningful — no grinding, no filler.
export const RECIPES: Recipe[] = [
  { id: 'pegs',    name: 'Wooden Pegs',        cost: { wood: 1 },              major: false, craftsmanship: 2,  minLevel: 1, desc: 'Fasteners for larger work.', yields: { peg: 4 } },
  { id: 'stool',   name: 'Stool',              cost: { wood: 2 },              major: false, craftsmanship: 6,  minLevel: 1, desc: 'A simple three-legged stool.' },
  { id: 'bucket',  name: 'Water Bucket Repair',cost: { wood: 1, rope: 1 },     major: false, craftsmanship: 5,  minLevel: 1, desc: 'Mend a household bucket.' },
  { id: 'toy',     name: "A Child's Toy",      cost: { wood: 1 },              major: false, craftsmanship: 4,  minLevel: 1, desc: 'A little cart from leftover wood.' },
  { id: 'plow',    name: 'Plow Repair',        cost: { wood: 2, peg: 1 },      major: false, craftsmanship: 8,  minLevel: 1, desc: 'A new share for the farmer.' },
  { id: 'bench',   name: 'Bench',              cost: { wood: 4 },              major: true,  craftsmanship: 12, minLevel: 2, desc: 'A sturdy bench for many to sit.' },
  { id: 'table',   name: 'Table',              cost: { wood: 4, peg: 2 },      major: true,  craftsmanship: 16, minLevel: 2, desc: 'A well-jointed household table.' },
  { id: 'chest',   name: 'Storage Chest',      cost: { wood: 3, rope: 1 },     major: true,  craftsmanship: 14, minLevel: 3, desc: 'A chest to keep a home tidy.' },
  { id: 'shelf',   name: 'Shelf',              cost: { wood: 3, peg: 1 },      major: false, craftsmanship: 10, minLevel: 3, desc: 'A shelf for the home.' },
  { id: 'door',    name: 'Door',               cost: { timber: 5 },            major: true,  craftsmanship: 22, minLevel: 4, desc: 'A fitted door of good timber.' },
  { id: 'cradle',  name: 'Cradle',             cost: { timber: 4 },            major: true,  craftsmanship: 24, minLevel: 4, desc: 'A gentle cradle for a new child.' },
  { id: 'beam',    name: 'Roof Beam',          cost: { timber: 5 },            major: true,  craftsmanship: 26, minLevel: 5, desc: 'A load-bearing beam for a roof.' },
];

export function recipeById(id: string): Recipe | undefined {
  return RECIPES.find((r) => r.id === id);
}

/** Recipes available at (or below) a given workshop level. */
export function recipesFor(level: number): Recipe[] {
  return RECIPES.filter((r) => r.minLevel <= level);
}

// ── Village requests ─────────────────────────────────────────────────────────
// Each request: talk → (gather &) craft the product → deliver it to the giver.
export interface Request {
  id: string;
  giver: string;         // npc id
  title: string;
  need: string;          // recipe id whose product satisfies it
  ask: string;           // what the villager says when asking
  thanks: string;        // what they say on delivery
  craftsmanship: number;
  charity: number;
  chapter: number;       // earliest chapter it can appear
  canGiveFree?: boolean; // poor villager: option to give freely (+charity)
  home?: boolean;        // counts as a "home & neighbor" project (Chapter III)
}

export const REQUESTS: Request[] = [
  { id: 'stool_hannah', giver: 'hannah', title: "Hannah's Stool", need: 'stool',
    ask: 'Joseph, my old stool has given way. Could you make me a new one?',
    thanks: 'Bless you — I shall sit easy now. Thank you, Joseph.',
    craftsmanship: 6, charity: 2, chapter: 1, canGiveFree: true },
  { id: 'plow_eli', giver: 'eli', title: "The Farmer's Plow", need: 'plow',
    ask: 'My plow is broken and the field waits. Can you repair it?',
    thanks: 'The soil will be turned before the rains. My thanks!',
    craftsmanship: 8, charity: 2, chapter: 2 },
  { id: 'toy_child', giver: 'child_ruth', title: "A Child's Toy", need: 'toy',
    ask: 'Master Joseph... could you make me a little cart? Please?',
    thanks: 'A cart! Thank you, thank you!',
    craftsmanship: 4, charity: 3, chapter: 2, canGiveFree: true },
  { id: 'bucket_susanna', giver: 'susanna', title: 'The Water Bucket', need: 'bucket',
    ask: 'Our bucket leaks and the well is far. Could you mend it?',
    thanks: 'Not a drop lost now. You are kind, Joseph.',
    craftsmanship: 5, charity: 2, chapter: 2, canGiveFree: true },
  { id: 'table_wedding', giver: 'reuben', title: 'The Wedding Table', need: 'table',
    ask: 'There is a wedding in three days. We need a table for the feast.',
    thanks: 'The finest table in Nazareth! The couple will bless your name.',
    craftsmanship: 16, charity: 3, chapter: 2 },
  { id: 'bench_synagogue', giver: 'rabbi', title: 'The Synagogue Bench', need: 'bench',
    ask: 'A bench in the synagogue is worn through. Would you make a new one?',
    thanks: 'The people will rest and pray upon your work. Shalom, Joseph.',
    craftsmanship: 12, charity: 4, chapter: 2 },
  { id: 'shelf_home', giver: 'mary', title: 'A Shelf for Home', need: 'shelf',
    ask: 'A shelf by the hearth would help me, Joseph, if you have the wood.',
    thanks: 'How well it fits. Thank you, my husband.',
    craftsmanship: 10, charity: 0, chapter: 3, home: true },
  { id: 'chest_home', giver: 'mary', title: 'A Chest for Home', need: 'chest',
    ask: 'A chest would keep our linens safe from the dust.',
    thanks: 'Our home grows dearer with your work. Thank you.',
    craftsmanship: 14, charity: 0, chapter: 3, home: true },
  { id: 'door_widow', giver: 'naomi', title: "The Widow's Door", need: 'door',
    ask: 'Since my husband died the door hangs broken. I have little to offer...',
    thanks: 'You asked nothing, yet gave much. God remember your kindness.',
    craftsmanship: 22, charity: 6, chapter: 4, canGiveFree: true },
  { id: 'beam_roof', giver: 'naomi', title: "The Widow's Roof", need: 'beam',
    ask: 'The roof beam is rotten and the rains are near. Can you help?',
    thanks: 'Dry and safe at last. Bless you, Joseph.',
    craftsmanship: 26, charity: 6, chapter: 4, canGiveFree: true },
  { id: 'cradle_family', giver: 'reuben', title: 'A New Cradle', need: 'cradle',
    ask: 'A child comes to our house. Would you build a cradle worthy of it?',
    thanks: 'Our little one will sleep in your kindness. Thank you.',
    craftsmanship: 24, charity: 4, chapter: 4 },
];

export function requestById(id: string): Request | undefined {
  return REQUESTS.find((r) => r.id === id);
}

// ── The community project (Chapter V) — the well canopy ──────────────────────
export interface CommunityStage { name: string; cost: Partial<Record<MaterialKey, number>>; }
export const COMMUNITY_PROJECT = {
  name: 'The Well Canopy',
  blurb: 'Nazareth would shelter its well from the summer sun. Many hands bring materials; Joseph brings his craft.',
  stages: [
    { name: 'Set the stone footings', cost: { stone: 4 } },
    { name: 'Raise the four posts',   cost: { timber: 4 } },
    { name: 'Frame the roof beams',   cost: { timber: 4, peg: 2 } },
    { name: 'Lay the shade and cloth',cost: { wood: 3, cloth: 2, rope: 2 } },
  ] as CommunityStage[],
};

// ── Chapters ─────────────────────────────────────────────────────────────────
export interface Chapter { n: number; roman: string; title: string; goal: string; }
export const CHAPTERS: Chapter[] = [
  { n: 1, roman: 'I',   title: 'The Workshop',            goal: 'Build your first workbench' },
  { n: 2, roman: 'II',  title: 'The Carpenter of Nazareth',goal: 'Complete 5 village requests' },
  { n: 3, roman: 'III', title: 'Home and Family',          goal: 'Care for home and neighbor' },
  { n: 4, roman: 'IV',  title: 'Master Craftsman',         goal: 'Complete a masterwork' },
  { n: 5, roman: 'V',   title: 'A Life of Faithful Work',  goal: 'Raise the well canopy with Nazareth' },
];

// ── Prayers & reflections (never presented as biblical quotation) ────────────
export const PRAYERS: string[] = [
  'Lord, make my hands faithful in small things,\nthat great things may be entrusted to them.',
  'For daily bread and daily work,\nfor Mary and for the child, I give You thanks.',
  'Teach me to labor and not to seek reward,\nsave that of knowing I do Your will.',
  'Keep this house in peace, O God of our fathers,\nand my heart quiet within it.',
  'Blessed are You who gives strength to the weary\nand rest to the hands that serve.',
];

// ── Codex: "Joseph's Story" scrolls ──────────────────────────────────────────
export type CodexCategory = 'The Gospels' | 'First-Century Life' | 'Catholic Tradition' | 'St. Joseph';
export interface CodexEntry { id: string; category: CodexCategory; title: string; body: string; }

export const CODEX: CodexEntry[] = [
  { id: 'righteous', category: 'The Gospels', title: 'A Righteous Man',
    body: 'The Gospel of Matthew calls Joseph "a righteous man" (Matthew 1:19). When he learned Mary was with child, he resolved to act quietly and justly, sparing her from shame.' },
  { id: 'dreams', category: 'The Gospels', title: 'The Dreams of Joseph',
    body: 'Scripture records that God guided Joseph through dreams — to take Mary as his wife, to flee to Egypt, and to return to the land of Israel. Joseph obeyed each time without a recorded word of his own.' },
  { id: 'egypt', category: 'The Gospels', title: 'The Flight into Egypt',
    body: 'Warned in a dream, Joseph rose in the night and took the child and his mother to Egypt for their safety (Matthew 2). He protected his family as a faithful guardian.' },
  { id: 'nazareth', category: 'The Gospels', title: 'Return to Nazareth',
    body: 'After Herod died, Joseph brought the family back and settled in Nazareth of Galilee (Matthew 2:23), where Jesus grew up.' },
  { id: 'carpenter_son', category: 'The Gospels', title: "The Carpenter's Son",
    body: 'The people of Nazareth knew Jesus as "the carpenter\'s son" (Matthew 13:55) and "the carpenter" (Mark 6:3) — Joseph\'s trade and reputation had shaped how the town saw the family.' },
  { id: 'tekton', category: 'First-Century Life', title: 'Joseph the Tekton',
    body: 'The Greek word for Joseph\'s trade is "tekton." Though traditionally translated "carpenter," it can mean a craftsman or builder more broadly — working wood, stone, and household repairs. So Joseph likely made doors, stools, tables, beams, and farm tools alike.' },
  { id: 'village', category: 'First-Century Life', title: 'A Galilean Village',
    body: 'First-century Nazareth was a small farming village of a few hundred people: cream limestone houses, olive groves, a well, and a synagogue. Families worked in wood, stone, weaving, and the land.' },
  { id: 'household', category: 'First-Century Life', title: 'Home and Trade',
    body: 'A craftsman\'s home and workshop were often one. Tools were few and precious; wood was gathered carefully — fallen branches, purchased timber, damaged trees — for it was not plentiful.' },
  { id: 'foster', category: 'Catholic Tradition', title: 'Guardian of the Redeemer',
    body: 'Catholic tradition honors Joseph as the chaste husband of Mary and the foster-father, or guardian, of Jesus — the one entrusted with protecting and providing for the Holy Family.' },
  { id: 'patron_workers', category: 'Catholic Tradition', title: 'Patron of Workers',
    body: 'The Church celebrates St. Joseph the Worker on May 1, honoring the dignity of ordinary labor. Joseph teaches that daily work, done faithfully, can be holy.' },
  { id: 'patron_church', category: 'St. Joseph', title: 'Patron of the Universal Church',
    body: 'In 1870 Blessed Pius IX declared St. Joseph Patron of the Universal Church. He is also invoked as patron of fathers, of families, of a happy death, and of workers.' },
  { id: 'go_to_joseph', category: 'St. Joseph', title: 'Go to Joseph',
    body: '"Ite ad Ioseph" — "Go to Joseph." The words once spoken of another Joseph in Egypt (Genesis 41:55) became a watchword of devotion: in need, turn to St. Joseph, the faithful provider.' },
];
